<script setup lang="ts">
/**
 * AiAssistantPage (T14 → F1 cards, Investment-4 Wave-1) — chat-style assistant.
 *
 * Operator types a question; the assistant uses tool calls to fetch real
 * numbers (campaign stats, period comparisons, recent broadcasts, draft
 * segments). F1: each tool call is rendered as a typed ACTION-CARD (via
 * AssistantToolCard) instead of raw JSON, with action buttons that call the
 * existing protected endpoints by HUMAN click only:
 *   • «Создать сегмент» → POST /v1/segments/save-from-nl
 *   • «Рассылка на сегмент» → create segment, then route to the broadcast
 *     wizard prefilled with ?segment_id=
 *   • «Открыть» рассылку → route to /broadcasts/:id
 * Write actions are hidden when the user lacks the role (tenant_admin+).
 * The assistant itself stays read-only (it never writes to the DB).
 */
import { ref, computed, nextTick, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useToast } from "../composables/useToast";
import { useAuthStore } from "../stores/auth";
import { useAssistantStore } from "../stores/assistant";
import { renderMarkdown } from "../lib/markdown";
import { suggestionsForRoute, pageLabelForRoute } from "../composables/assistantSuggestions";
import { aiAssistantApi, type AiCostSummary, type AssistantTurn, type ActionChip } from "../api/ai-assistant";
import { nlSegmentApi } from "../api/nl-segment";
import { journeysApi } from "../api/journeys";
import { surveysApi, type ScaleType } from "../api/surveys";
import type { JourneyDraftPayload, SurveyDraftPayload, ExperienceDraftPayload } from "../components/ai/assistant-card-model";
import {
  readDraftSegment,
  readDraftBroadcast,
  readJourneyDraft,
  readSurveyDraft,
  readExperienceDraft,
} from "../components/ai/assistant-card-model";
import { ApiError } from "../api/client";
import { formatKopecks } from "../utils/number-format";
import { Send, Bot, User as UserIcon, X, Wallet, AlertTriangle, MapPin, Lightbulb, Sparkles } from "lucide-vue-next";
import PageHelp from "../components/ui/PageHelp.vue";
import AssistantToolCard from "../components/ai/AssistantToolCard.vue";
import AiCostBadge from "../components/ai/AiCostBadge.vue";

const { t } = useI18n();
const toast = useToast();
const router = useRouter();
const auth = useAuthStore();
const { role } = storeToRefs(auth);

// Write actions (create segment / broadcast) are admin-only — same RBAC as the
// direct actions. Operators see cards but not the write buttons.
const canWrite = computed(() => role.value === "super_admin" || role.value === "tenant_admin");

// Embedded = rendered inside the floating widget's slide-over (FIX-AI-6): drop
// the full-page chrome (title block, PageHelp) and fill the panel height.
defineProps<{ embedded?: boolean }>();

const route = useRoute();
// Chat lives in the store so it survives navigation + reload and is shared with
// the floating widget (FIX-AI-6).
const assistant = useAssistantStore();
const { history, prompt } = storeToRefs(assistant);
const sending = ref(false);
// 1b — current tool gerund label while streaming («Ищу опросы…»); replaces the
// static «AI-маркетолог думает…» progress line. Empty = generic thinking line.
const streamLabel = ref("");
const scrollerRef = ref<HTMLElement | null>(null);

// D4 — расход на ИИ за сегодня vs дневной бюджет (копейки) + активный провайдер.
const cost = ref<AiCostSummary | null>(null);
// Дневной лимит исчерпан — показываем встроенное уведомление вместо краша.
const budgetExceeded = ref(false);

// Человекочитаемое имя провайдера LLM для подписи «AI сегодня».
const PROVIDER_LABELS: Record<string, string> = {
  anthropic: "Anthropic",
  yandex: "YandexGPT",
  gigachat: "GigaChat",
  mock: t("ai_cost.provider_mock"),
};
const providerLabel = computed(() =>
  cost.value ? (PROVIDER_LABELS[cost.value.provider] ?? cost.value.provider) : "",
);

// «AI сегодня: X,XX ₽ / Y,YY ₽» (или «… / без лимита» когда бюджет null).
const spentLabel = computed(() => (cost.value ? formatKopecks(cost.value.spent_kopecks) : ""));
const budgetLabel = computed(() => {
  if (!cost.value) return "";
  return cost.value.budget_kopecks == null ? t("ai_cost.no_limit") : formatKopecks(cost.value.budget_kopecks);
});

async function loadCost(): Promise<void> {
  try {
    cost.value = await aiAssistantApi.costSummary();
  } catch {
    // Сводка расходов необязательна — молча скрываем чип, чат работает дальше.
    cost.value = null;
  }
}
// Per tool-call raw-JSON toggle, keyed "<turnIdx>:<callIdx>".
const rawOpen = ref<Record<string, boolean>>({});

// Save-segment modal state.
const savePrompt = ref<{
  filter: Record<string, unknown>;
  name: string;
  thenBroadcast: boolean;
  /** When coming from a draft_broadcast Apply: the proposed broadcast name to
   *  pass to the wizard via ?name= (the segment name itself stays editable). */
  broadcastName?: string;
} | null>(null);
const saving = ref(false);

const canSend = computed(() => prompt.value.trim().length > 0 && !sending.value);

/**
 * Light page-context (FIX-AI-6): tell the assistant which page the operator is
 * on so it can be more relevant. Resolves the current route to its nav label.
 */
function currentPageContext(): string {
  const label = pageLabelForRoute(route?.path, t);
  return label ? `(контекст: оператор сейчас на странице «${label}»)` : "";
}

// Page-aware "prompt starters" (verified to fire a real tool, no errors). The
// context pill shows which page they're for; clicking only fills the input.
const suggestions = computed(() => suggestionsForRoute(route?.path));
const pageLabel = computed(() => pageLabelForRoute(route?.path, t));

async function scrollToBottom(): Promise<void> {
  await nextTick();
  if (scrollerRef.value) {
    scrollerRef.value.scrollTop = scrollerRef.value.scrollHeight;
  }
}

/** Treat the 409 daily-budget cap the same way for both stream + fallback. */
function isBudgetExceeded(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409 && e.code === "AI_BUDGET_EXCEEDED";
}

/** Friendly inline budget notice + restore the prompt (no answer is coming). */
async function handleBudgetExceeded(userMessage: string): Promise<void> {
  budgetExceeded.value = true;
  // Снимаем «пустой» пользовательский вопрос обратно в поле ввода — ответа не
  // будет, оставлять висящую реплику в ленте незачем.
  if (history.value[history.value.length - 1]?.role === "user") history.value.pop();
  prompt.value = userMessage;
  void loadCost();
  await scrollToBottom();
}

/** Non-stream fallback (ask()) — the original behavior, reused on stream failure. */
async function sendViaAsk(promptText: string): Promise<void> {
  const data = await aiAssistantApi.ask({
    prompt: promptText,
    history: history.value.slice(0, -1),
  });
  if (data.ai_cost != null) data.turn.ai_cost = data.ai_cost; // §H — inline cost badge
  history.value.push(data.turn);
  void loadCost();
  await scrollToBottom();
}

async function send(): Promise<void> {
  if (!canSend.value) return;
  // Set sending SYNCHRONOUSLY before the first await — closes the double-click
  // race where a 2nd suggestion click during the scroll-yield re-armed the
  // prompt and fired a duplicate request/turn (QA finding).
  sending.value = true;
  streamLabel.value = "";
  budgetExceeded.value = false;
  const userMessage = prompt.value.trim();
  prompt.value = "";
  history.value.push({ role: "user", content: userMessage });
  await scrollToBottom();

  const ctx = currentPageContext();
  const promptText = ctx ? `${ctx}\n\n${userMessage}` : userMessage;
  // History sent to the engine must NOT include the just-pushed user turn — it's
  // passed as `prompt`. (Mirrors the old ask() slice(0, -1).)
  const sentHistory = history.value.slice(0, -1);

  // Append the live assistant turn we stream into. We track whether ANY output
  // (text or tool result) landed; on a stream error with nothing yet, we drop
  // this turn and transparently fall back to the non-stream ask().
  const liveTurn: AssistantTurn = { role: "assistant", content: "", tool_calls: [], citations: [] };
  history.value.push(liveTurn);
  const liveIdx = history.value.length - 1;
  let received = false;
  let doneFired = false;
  // Classify the outcome in the (sync) handlers / catch; ONE decision block below
  // acts on it. Splitting these prevents the old retry storm where an in-band
  // engine error fell back to ask(), that ask() also failed, and the outer catch
  // fell back to ask() AGAIN (3 backend hits + up to 2×120s wait per message).
  let budgetSignaled = false;                          // 409 — frame or thrown pre-gate
  let engineFailed = false;                            // in-band AI_ASSISTANT_FAILED → surface once, NO retry
  let engineMsg = "";                                  // its message (separate var — TS can't narrow a closure-assigned union)
  let lateThrow: unknown = null;                        // threw AFTER partial output → keep partial, toast once
  let turnCost: number | null = null;                   // §H — cost of this turn (kopecks), from the `usage` event
  // NO_STREAM / STREAM_ABORTED / empty stream leave all flags false → the
  // catch-all `else` does ONE ask() retry (streaming-specific failure).

  /** Mutate the live turn in place (history is reactive; reassign the slot). */
  function patchLive(fn: (t: AssistantTurn) => void): void {
    const t = history.value[liveIdx];
    if (!t) return;
    fn(t);
  }

  try {
    await aiAssistantApi.askStream(
      { prompt: promptText, history: sentHistory },
      {
        onToolCall: (d) => {
          received = true;
          streamLabel.value = d.label || "";
          // Show a «running» placeholder card so the operator sees progress; it's
          // replaced by the full tool_calls list when `done` arrives.
          patchLive((t) => {
            t.tool_calls = [...(t.tool_calls ?? []), { tool: d.tool, input: d.input, output: undefined }];
          });
          void scrollToBottom();
        },
        onToolResult: () => {
          received = true;
          // Result detail rides in the final `done` turn; nothing to render here
          // beyond keeping the progress line alive.
        },
        onTextDelta: (d) => {
          received = true;
          // Text has started — drop the tool progress label.
          streamLabel.value = "";
          patchLive((t) => { t.content += d.delta ?? ""; });
          void scrollToBottom();
        },
        onUsage: (d) => {
          // §H — `usage` arrives just before `done`; stash the cost to attach below.
          turnCost = d.ai_cost;
        },
        onDone: (d) => {
          doneFired = true;
          received = true;
          streamLabel.value = "";
          // Replace the live turn with the authoritative one (tool_calls+citations).
          if (d.turn) {
            if (turnCost != null) d.turn.ai_cost = turnCost; // §H — inline cost badge
            history.value.splice(liveIdx, 1, d.turn);
          }
          void loadCost();
          void scrollToBottom();
        },
        onError: (e) => {
          // Handlers are sync — classify only; the decision block below acts ONCE.
          if (e.error === "AI_BUDGET_EXCEEDED") budgetSignaled = true;
          else if (e.error === "AI_ASSISTANT_FAILED") { engineFailed = true; engineMsg = e.message || ""; }
          // else: transport (NO_STREAM / STREAM_ABORTED) → leave flags false → fallback.
        },
      },
    );
  } catch (e: unknown) {
    // askStream threw — the budget 409 pre-gate (JSON, before the stream) or a
    // fetch/network failure. Classify; the single decision block below acts.
    if (isBudgetExceeded(e)) budgetSignaled = true;
    else if (received) lateThrow = e;     // partial output already shown
    // else: couldn't stream at all → leave flags false → fallback to ask() once.
  }

  // ── Single decision point (outside try/catch ⇒ AT MOST one ask() fallback) ──
  try {
    if (budgetSignaled) {
      history.value.splice(liveIdx, 1); // drop the empty assistant turn
      await handleBudgetExceeded(userMessage);
    } else if (doneFired) {
      // Authoritative turn already applied in onDone — nothing more to do.
    } else if (engineFailed) {
      // The backend started the stream then failed in-band. Retrying the SAME
      // backend via ask() is pointless — surface it once, no fallback.
      history.value.splice(liveIdx, 1);
      toast.push({ kind: "error", message: engineMsg || "Ассистент временно недоступен. Попробуйте позже." });
    } else if (received) {
      // Partial answer (text/cards but no `done`) — keep it; toast a late throw.
      if (lateThrow) {
        toast.push({ kind: "error", message: lateThrow instanceof ApiError ? lateThrow.message : (lateThrow as Error).message });
      }
      void loadCost();
      await scrollToBottom();
    } else {
      // transportFailed OR an empty/closed stream → ONE graceful fallback to ask().
      history.value.splice(liveIdx, 1);
      try {
        await sendViaAsk(promptText);
      } catch (e2: unknown) {
        if (isBudgetExceeded(e2)) await handleBudgetExceeded(userMessage);
        else toast.push({ kind: "error", message: e2 instanceof ApiError ? e2.message : (e2 as Error).message });
      }
    }
  } finally {
    sending.value = false;
    streamLabel.value = "";
  }
}

function rawKey(turnIdx: number, callIdx: number): string {
  return `${turnIdx}:${callIdx}`;
}
function toggleRaw(turnIdx: number, callIdx: number): void {
  const k = rawKey(turnIdx, callIdx);
  rawOpen.value[k] = !rawOpen.value[k];
}

function clearHistory(): void {
  history.value = [];
  rawOpen.value = {};
}

// ─── Card actions ───
function openCreateSegment(payload: { filter: Record<string, unknown>; suggestedName: string }, thenBroadcast: boolean): void {
  savePrompt.value = { filter: payload.filter, name: payload.suggestedName, thenBroadcast };
}

async function confirmSaveSegment(): Promise<void> {
  if (!savePrompt.value) return;
  const name = savePrompt.value.name.trim();
  if (!name) {
    toast.push({ kind: "error", message: "Введите название сегмента." });
    return;
  }
  saving.value = true;
  try {
    const seg = await nlSegmentApi.saveFromNL({
      prompt_used: name,
      filter: savePrompt.value.filter,
      name,
      description: null,
    });
    const thenBroadcast = savePrompt.value.thenBroadcast;
    const broadcastName = savePrompt.value.broadcastName?.trim();
    savePrompt.value = null;
    toast.push({ kind: "success", message: "Сегмент сохранён." });
    if (thenBroadcast) {
      router.push({
        name: "broadcast-new",
        query: { segment_id: seg.id, ...(broadcastName ? { name: broadcastName } : {}) },
      });
    } else {
      // Свежесозданный сегмент открываем в РЕДАКТОРЕ для уточнения фильтра
      // (канон навигации Фазы 4: /segments/:id/edit).
      router.push({ name: "segment-edit", params: { id: seg.id } });
    }
  } catch (e: unknown) {
    if (e instanceof ApiError && e.code === "NAME_TAKEN") {
      toast.push({ kind: "error", message: "Сегмент с таким названием уже существует." });
    } else {
      toast.push({ kind: "error", message: e instanceof ApiError ? e.message : (e as Error).message });
    }
  } finally {
    saving.value = false;
  }
}

function openBroadcast(id: string): void {
  if (!id) return;
  router.push({ name: "broadcast-detail", params: { id } });
}

// ─── Copilot Apply: draft_broadcast → save segment, then open the wizard ───
// Reuses the save-segment-then-broadcast flow (the human reviews/renames the
// segment in the modal). The proposed broadcast name rides along to the wizard
// via ?name=. The body_text stays on the card for the operator to copy — the
// actual message depends on the channel/template the human picks, so we don't
// force it into the wizard. The AI never persists or sends.
function openDraftBroadcast(payload: { filter: Record<string, unknown>; suggestedName: string; bodyText: string }): void {
  savePrompt.value = {
    filter: payload.filter,
    // Seed the segment name from the proposed broadcast name (editable in modal).
    name: payload.suggestedName || "Сегмент из ИИ",
    thenBroadcast: true,
    broadcastName: payload.suggestedName,
  };
}

// ─── Copilot Apply: create_experience → open the web-tool editor prefilled ───
// Encodes the draft UTF-8-safe (Russian text survives btoa) and navigates to the
// experience editor PAGE (/experiences/new), which decodes ?draft= and prefills the form.
// The AI never persists the experience; the human saves it in the editor.
function openCreateExperience(payload: { draft: ExperienceDraftPayload }): void {
  try {
    const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(payload.draft))));
    router.push({ name: "experience-new", query: { draft: b64 } });
  } catch (e: unknown) {
    toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
  }
}

// ─── D3: Copilot proposal applies (approve step) ───
// Each Apply opens a confirm modal (name editable) and only persists on the
// human's click via the protected route. The card itself never writes.

// create_journey → POST /v1/journeys (draft). Modal lets the human rename.
const journeyApply = ref<{ draft: JourneyDraftPayload; name: string } | null>(null);
const applyingJourney = ref(false);

function openCreateJourney(payload: { draft: JourneyDraftPayload }): void {
  journeyApply.value = { draft: payload.draft, name: payload.draft.name };
}

async function confirmCreateJourney(): Promise<void> {
  if (!journeyApply.value) return;
  const name = journeyApply.value.name.trim();
  if (!name) {
    toast.push({ kind: "error", message: t("ai_cards.create_journey.name_label") });
    return;
  }
  applyingJourney.value = true;
  try {
    const d = journeyApply.value.draft;
    const journey = await journeysApi.create({
      name,
      entry_event_type: d.entry_event_type,
      reentry_policy: (d.reentry_policy as never) || undefined,
      flow: (d.flow as never) ?? [],
    });
    journeyApply.value = null;
    toast.push({ kind: "success", message: t("ai_cards.create_journey.saved") });
    router.push({ name: "journey-detail", params: { id: journey.id } });
  } catch (e: unknown) {
    if (e instanceof ApiError && e.code === "NAME_TAKEN") {
      toast.push({ kind: "error", message: t("ai_cards.create_journey.name_taken") });
    } else {
      toast.push({ kind: "error", message: e instanceof ApiError ? e.message : (e as Error).message });
    }
  } finally {
    applyingJourney.value = false;
  }
}

// publish_survey → POST /v1/surveys (active). Modal lets the human rename.
const surveyApply = ref<{ draft: SurveyDraftPayload; name: string } | null>(null);
const applyingSurvey = ref(false);

function openPublishSurvey(payload: { draft: SurveyDraftPayload }): void {
  surveyApply.value = { draft: payload.draft, name: payload.draft.name };
}

async function confirmPublishSurvey(): Promise<void> {
  if (!surveyApply.value) return;
  const name = surveyApply.value.name.trim();
  if (!name) {
    toast.push({ kind: "error", message: t("ai_cards.publish_survey.name_label") });
    return;
  }
  applyingSurvey.value = true;
  try {
    const d = surveyApply.value.draft;
    const survey = await surveysApi.create({
      name,
      scale_type: d.scale_type as ScaleType,
      question: d.question,
      ...(d.detractor_max !== undefined ? { detractor_max: d.detractor_max } : {}),
      ...(d.promoter_min !== undefined ? { promoter_min: d.promoter_min } : {}),
      ...(d.ask_comment !== undefined ? { ask_comment: d.ask_comment } : {}),
      active: true,
    });
    surveyApply.value = null;
    toast.push({ kind: "success", message: t("ai_cards.publish_survey.saved") });
    router.push({ name: "survey-detail", params: { id: survey.id } });
  } catch (e: unknown) {
    if (e instanceof ApiError && e.code === "NAME_TAKEN") {
      toast.push({ kind: "error", message: t("ai_cards.publish_survey.name_taken") });
    } else {
      toast.push({ kind: "error", message: e instanceof ApiError ? e.message : (e as Error).message });
    }
  } finally {
    applyingSurvey.value = false;
  }
}

function openOffer(id: string): void {
  if (!id) return;
  router.push({ name: "offers" });
}

// ─── Phase-1 READ tools — optional «Открыть» navigation (no write) ───
function openSurvey(id: string): void {
  if (!id) return;
  router.push({ name: "survey-detail", params: { id } });
}
function openProfile(id: string): void {
  if (!id) return;
  router.push({ name: "profile-detail", params: { id } });
}

// 1c — page-suggestion chips are actionable: clicking one SENDS it immediately
// (fill the input + fire send), rather than only filling the textarea. `send()`
// is a no-op while another request is in flight (guarded by canSend).
function useExample(p: string): void {
  if (sending.value) return; // ignore clicks while a request is in flight (no dup turn)
  prompt.value = p;
  void send();
}

// ─── 1c — action-chips on the assistant turn ───
//
// The engine emits `turn.actions: ActionChip[]`; the PAYLOAD rides in the
// matching `turn.tool_calls` entry (chip.tool → tool_calls.find). We resolve the
// output, run the matching card-model reader and dispatch to the SAME apply-
// handler the cards use (all of which go through the preview/approve modal or a
// prefilled-editor navigation — never a silent write).
//
// Every step is defensive: a missing tool_call, a null output, or a reader that
// returns no filter/draft → the chip is treated as not-actionable (disabled),
// and runActionChip is a guarded no-op (it must never throw — a past bug
// white-screened the chat on a `.slice` of a non-string).

/** Resolve the source tool_call output for a chip (undefined when absent). */
function chipOutput(turn: AssistantTurn, chip: ActionChip): unknown {
  // Resolve by the source tool_call INDEX so two same-tool drafts in one turn
  // each map to their own call; fall back to first-match-by-tool for older
  // persisted turns that predate toolIndex.
  const byIndex = typeof chip.toolIndex === "number" ? turn.tool_calls?.[chip.toolIndex] : undefined;
  return (byIndex ?? turn.tool_calls?.find((tc) => tc.tool === chip.tool))?.output;
}

/**
 * True when the chip's source tool_call carries a usable payload (filter/draft).
 * Used to disable chips whose payload is missing/malformed instead of letting a
 * click no-op silently. Wrapped in try/catch — a reader must never throw here.
 */
function isActionChipReady(turn: AssistantTurn, chip: ActionChip): boolean {
  try {
    const output = chipOutput(turn, chip);
    if (output == null) return false;
    switch (chip.kind) {
      case "create_segment":
      case "broadcast_on_segment":
        return readDraftSegment(output).filter != null;
      case "draft_broadcast":
        return readDraftBroadcast(output).filter != null;
      case "create_journey":
        return readJourneyDraft(output).draft != null;
      case "publish_survey":
        return readSurveyDraft(output).draft != null;
      case "create_experience":
        return readExperienceDraft(output).draft != null;
      default:
        return false; // unknown kind — not actionable
    }
  } catch {
    return false;
  }
}

/** Dispatch a chip click to the matching apply-handler (guarded; never throws). */
function runActionChip(turn: AssistantTurn, chip: ActionChip): void {
  try {
    const output = chipOutput(turn, chip);
    if (output == null) return;
    switch (chip.kind) {
      case "create_segment": {
        const v = readDraftSegment(output);
        if (!v.filter) return;
        openCreateSegment({ filter: v.filter, suggestedName: v.suggestedName }, false);
        break;
      }
      case "broadcast_on_segment": {
        const v = readDraftSegment(output);
        if (!v.filter) return;
        openCreateSegment({ filter: v.filter, suggestedName: v.suggestedName }, true);
        break;
      }
      case "draft_broadcast": {
        const v = readDraftBroadcast(output);
        if (!v.filter) return;
        openDraftBroadcast({ filter: v.filter, suggestedName: v.name, bodyText: v.bodyText });
        break;
      }
      case "create_journey": {
        const v = readJourneyDraft(output);
        if (!v.draft) return;
        openCreateJourney({ draft: v.draft });
        break;
      }
      case "publish_survey": {
        const v = readSurveyDraft(output);
        if (!v.draft) return;
        openPublishSurvey({ draft: v.draft });
        break;
      }
      case "create_experience": {
        const v = readExperienceDraft(output);
        if (!v.draft) return;
        openCreateExperience({ draft: v.draft });
        break;
      }
      default:
        // Unknown kind — ignore (forward-compat).
        break;
    }
  } catch (e: unknown) {
    // Never let a malformed payload crash the chat — surface a soft toast.
    toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
  }
}

function onKeydown(e: KeyboardEvent): void {
  // Enter sends, Shift+Enter inserts newline.
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    void send();
  }
}

onMounted(() => {
  void loadCost();
  // #1 — restore the view to the latest messages on (re)open. History is
  // restored from the persisted store, but the scroller starts at the top;
  // chat UX expects the newest turn in view, not the first.
  void scrollToBottom();
});
</script>

<template>
  <div :class="embedded ? 'h-full flex flex-col' : 'max-w-5xl mx-auto h-[calc(100vh-8rem)] flex flex-col'">
    <div class="flex items-center justify-between mb-4">
      <div v-if="!embedded">
        <h1 class="text-2xl font-semibold text-surface-900">AI-маркетолог</h1>
        <p class="text-sm text-surface-500 mt-1">
          Помощник на основе Claude. Спросите про метрики кампаний, попросите построить сегмент или сравнить периоды.
        </p>
      </div>
      <div class="flex items-center gap-3">
        <!-- Контекст текущей страницы — подсказки ниже подобраны под неё (FIX-AI-6). -->
        <span
          v-if="pageLabel"
          class="inline-flex items-center gap-1 rounded-md bg-accent-500/10 text-accent-600 px-2 py-1 text-xs font-medium"
          data-test="ai-context-pill"
          :title="`Подсказки подобраны под страницу «${pageLabel}»`"
        >
          <MapPin class="size-3.5" /> {{ pageLabel }}
        </span>
        <!-- D4 — расход на ИИ за сегодня vs дневной бюджет. -->
        <div
          v-if="cost"
          data-test="ai-cost"
          class="inline-flex items-center gap-1.5 rounded-md border border-surface-200 bg-surface-0 px-2.5 py-1.5 text-xs"
          :title="t('ai_cost.tooltip', { provider: providerLabel })"
        >
          <Wallet class="size-3.5 text-surface-400" />
          <span class="text-surface-500">{{ t("ai_cost.today") }}:</span>
          <span class="font-medium tabular-nums text-surface-800">{{ spentLabel }}</span>
          <span class="text-surface-400">/</span>
          <span class="tabular-nums text-surface-600">{{ budgetLabel }}</span>
        </div>
        <button
          v-if="history.length > 0"
          class="btn-secondary text-sm"
          @click="clearHistory"
        >
          Очистить
        </button>
      </div>
    </div>

    <PageHelp v-if="!embedded" storage-key="page-help-ai-assistant" title="Помощник ИИ — маркетолог">
      <p>Это <strong>помощник ИИ</strong>, у которого есть инструменты для запросов к CDP:</p>
      <ul class="list-disc pl-5 space-y-0.5">
        <li><code>get_campaign_stats</code> — показатели рассылки или сценария.</li>
        <li><code>compare_periods</code> — сравнить «эту неделю vs прошлую».</li>
        <li><code>list_recent_broadcasts</code> — что отправлялось.</li>
        <li><code>draft_segment</code> — построить фильтр сегмента по описанию.</li>
        <li><code>draft_broadcast</code> — собрать черновик рассылки (сегмент + текст) и открыть мастер.</li>
        <li><code>create_experience</code> — собрать черновик веб-инструмента (попап/опрос) и открыть редактор.</li>
        <li><code>explain_decision</code> — объяснить простыми словами, почему принято то или иное решение.</li>
      </ul>
      <p class="mt-2"><strong>Примеры запросов</strong>:</p>
      <ul class="list-disc pl-5 space-y-0.5">
        <li>«Какая лучшая рассылка прошлой недели по доле открытий?»</li>
        <li>«Сравни этот месяц с прошлым: доставлено, конверсии, выручка».</li>
        <li>«Построй сегмент: VIP — 5+ заказов или сумма ≥ 5000₽ за 60 дней».</li>
      </ul>
      <p class="mt-2 text-surface-600 text-xs">Запросы и ответы видны в <em>журнале аудита</em>. Инструменты вызываются с изоляцией по организации.</p>
    </PageHelp>

    <div
      ref="scrollerRef"
      class="card flex-1 overflow-y-auto p-4 space-y-4 bg-surface-50/40"
    >
      <div v-if="history.length === 0" class="text-center py-12">
        <Bot class="size-12 mx-auto text-surface-300 mb-3" />
        <p class="text-surface-500 mb-4">
          Начните разговор<template v-if="pageLabel"> — подсказки для страницы «{{ pageLabel }}»</template>:
        </p>
        <div class="flex flex-col gap-2 max-w-md mx-auto">
          <button
            v-for="s in suggestions"
            :key="s.prompt"
            class="flex items-start gap-2 text-left text-sm px-4 py-2.5 rounded border border-surface-200 hover:bg-surface-50 hover:border-accent-300"
            data-test="ai-suggestion-card"
            @click="useExample(s.prompt)"
          >
            <Lightbulb class="size-4 text-accent-500 shrink-0 mt-0.5" />
            <span>{{ s.prompt }}</span>
          </button>
        </div>
      </div>

      <div
        v-for="(turn, idx) in history"
        :key="idx"
        class="flex gap-3"
      >
        <div
          class="flex-shrink-0 size-8 rounded-full flex items-center justify-center"
          :class="turn.role === 'user' ? 'bg-accent-100 text-accent-700' : 'bg-success-100 text-success-700'"
        >
          <UserIcon v-if="turn.role === 'user'" class="size-4" />
          <Bot v-else class="size-4" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-xs text-surface-400 font-medium mb-1">
            {{ turn.role === "user" ? "Вы" : "AI-маркетолог" }}
          </div>
          <div
            class="rounded-lg p-3 text-sm"
            :class="turn.role === 'user'
              ? 'bg-accent-50 border border-accent-100 whitespace-pre-wrap'
              : 'bg-surface-0 border border-surface-200'"
          >
            <!-- User text is shown literally (never parsed as markdown/HTML).
                 Assistant turns are rendered from sanitized Markdown. -->
            <template v-if="turn.role === 'user'">{{ turn.content }}</template>
            <div v-else class="md-content" v-html="renderMarkdown(turn.content)" />
          </div>

          <!-- F1 — typed action-cards instead of raw JSON. -->
          <div v-if="turn.tool_calls && turn.tool_calls.length > 0" class="mt-2 space-y-2">
            <AssistantToolCard
              v-for="(tc, tci) in turn.tool_calls"
              :key="tci"
              :call="tc"
              :can-write="canWrite"
              :raw-open="!!rawOpen[rawKey(idx, tci)]"
              @toggle-raw="toggleRaw(idx, tci)"
              @create-segment="openCreateSegment($event, false)"
              @broadcast-on-segment="openCreateSegment($event, true)"
              @open-broadcast="openBroadcast"
              @create-journey="openCreateJourney"
              @publish-survey="openPublishSurvey"
              @open-offer="openOffer"
              @draft-broadcast="openDraftBroadcast"
              @create-experience="openCreateExperience"
              @open-survey="openSurvey"
              @open-profile="openProfile"
            />
          </div>

          <!-- 1c — «Действия:» row (clickable action-chips). Only on assistant
               turns; all chips are writes-via-approve, so the whole row follows
               the same canWrite gate as the card write-buttons. -->
          <div
            v-if="turn.role === 'assistant' && canWrite && turn.actions && turn.actions.length"
            class="mt-2 flex items-center gap-1.5 flex-wrap"
            data-test="ai-actions"
          >
            <span class="text-xs text-surface-400 shrink-0">{{ t("ai_assistant.actions") }}:</span>
            <button
              v-for="(chip, ai) in turn.actions"
              :key="ai"
              type="button"
              class="inline-flex items-center gap-1 text-xs font-medium rounded-full border border-accent-500/30 bg-accent-500/10 text-accent-600 px-2.5 py-1 transition-colors hover:bg-accent-500/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-accent-500/10"
              data-test="ai-action-chip"
              :disabled="!isActionChipReady(turn, chip)"
              @click="runActionChip(turn, chip)"
            >
              <Sparkles class="size-3" />
              {{ chip.label || t("ai_assistant.action_fallback") }}
            </button>
          </div>

          <!-- 1d — «Источники:» row (show-your-work). Only on assistant turns. -->
          <div
            v-if="turn.role === 'assistant' && turn.citations && turn.citations.length"
            class="mt-2 flex items-center gap-1.5 flex-wrap"
            data-test="ai-citations"
          >
            <span class="text-xs text-surface-400 shrink-0">{{ t("ai_assistant.sources") }}:</span>
            <span
              v-for="(c, ci) in turn.citations"
              :key="ci"
              class="inline-flex items-center gap-1 text-xs rounded-full bg-surface-100 text-surface-600 px-2 py-0.5"
              data-test="ai-citation-chip"
            >
              {{ c.label }}<span v-if="c.count != null" class="text-surface-400">({{ c.count }})</span>
            </span>
          </div>

          <!-- §H — per-turn AI cost (kopecks), shown to all roles. -->
          <div
            v-if="turn.role === 'assistant' && turn.ai_cost != null"
            class="mt-2"
            data-test="ai-turn-cost"
          >
            <AiCostBadge :kopecks="turn.ai_cost" />
          </div>
        </div>
      </div>

      <div v-if="sending" class="flex gap-3">
        <div class="flex-shrink-0 size-8 rounded-full bg-success-100 text-success-700 flex items-center justify-center">
          <Bot class="size-4" />
        </div>
        <div class="text-sm text-surface-500 pt-1" data-test="ai-progress">
          <span class="animate-pulse">{{ streamLabel || "AI-маркетолог думает…" }}</span>
        </div>
      </div>

      <!-- D4 — дневной лимит расходов на ИИ исчерпан. -->
      <div
        v-if="budgetExceeded"
        data-test="ai-budget-exceeded"
        class="flex items-start gap-3 rounded-lg border border-warning-500/30 bg-warning-500/10 p-3"
      >
        <AlertTriangle class="size-5 flex-shrink-0 text-warning-600 mt-0.5" />
        <div class="text-sm">
          <p class="font-medium text-warning-600">{{ t("ai_cost.exceeded_title") }}</p>
          <p class="text-surface-600 mt-0.5">{{ t("ai_cost.exceeded_body") }}</p>
        </div>
      </div>
    </div>

    <!-- Контекстные подсказки над полем — переписка выше остаётся (FIX-AI-6). -->
    <div
      v-if="history.length > 0 && suggestions.length"
      class="mt-3 flex items-center gap-2 flex-wrap"
      data-test="ai-suggestion-strip"
    >
      <span class="inline-flex items-center gap-1 text-xs text-surface-400 shrink-0">
        <Lightbulb class="size-3.5" /> Ещё для этой страницы:
      </span>
      <button
        v-for="s in suggestions"
        :key="s.prompt"
        class="text-xs bg-accent-500/10 text-accent-600 hover:bg-accent-500/20 rounded-full px-2.5 py-1 transition-colors"
        data-test="ai-suggestion-chip"
        @click="useExample(s.prompt)"
      >
        {{ s.label }}
      </button>
    </div>

    <div class="mt-4">
      <div class="flex items-end gap-2">
        <textarea
          v-model="prompt"
          rows="2"
          class="input flex-1 resize-none"
          placeholder="Задайте вопрос… (Enter — отправить, Shift+Enter — перенос строки)"
          :disabled="sending"
          @keydown="onKeydown"
        ></textarea>
        <button
          class="btn-primary inline-flex items-center gap-2 px-4 py-2"
          :disabled="!canSend"
          @click="send"
        >
          <Send class="size-4" /> Отправить
        </button>
      </div>
    </div>

    <!-- Save-segment modal (triggered by «Создать сегмент» / «Рассылка на сегмент») -->
    <div
      v-if="savePrompt"
      class="fixed inset-0 z-50 flex items-center justify-center bg-surface-900/40 p-4"
      @click.self="savePrompt = null"
    >
      <div class="card p-5 w-full max-w-md space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-surface-900">
            {{ savePrompt.thenBroadcast ? "Сегмент для рассылки" : "Создать сегмент" }}
          </h2>
          <button class="text-surface-400 hover:text-surface-700" @click="savePrompt = null">
            <X class="size-5" />
          </button>
        </div>
        <label class="block">
          <span class="text-sm font-medium text-surface-700 mb-1 block">Название сегмента</span>
          <input
            v-model="savePrompt.name"
            type="text"
            class="input w-full"
            placeholder="Например: открыли письмо за 7 дней"
            @keydown.enter="confirmSaveSegment"
          />
        </label>
        <p class="text-xs text-surface-500">
          {{ savePrompt.thenBroadcast
            ? "Сегмент сохранится, затем откроется мастер рассылки на этот сегмент."
            : "Сегмент сохранится с исходным фильтром. Затем откроется его карточка." }}
        </p>
        <div class="flex items-center justify-end gap-2">
          <button class="btn-secondary" @click="savePrompt = null">{{ "Отмена" }}</button>
          <button class="btn-primary inline-flex items-center gap-1.5" :disabled="saving" @click="confirmSaveSegment">
            {{ saving ? "Сохранение…" : "Сохранить" }}
          </button>
        </div>
      </div>
    </div>

    <!-- D3 — create_journey approve modal («Применить» on a journey draft) -->
    <div
      v-if="journeyApply"
      data-test="journey-apply-modal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-surface-900/40 p-4"
      @click.self="journeyApply = null"
    >
      <div class="card p-5 w-full max-w-md space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-surface-900">{{ t("ai_cards.create_journey.modal_title") }}</h2>
          <button class="text-surface-400 hover:text-surface-700" @click="journeyApply = null">
            <X class="size-5" />
          </button>
        </div>
        <label class="block">
          <span class="text-sm font-medium text-surface-700 mb-1 block">{{ t("ai_cards.create_journey.name_label") }}</span>
          <input
            v-model="journeyApply.name"
            type="text"
            class="input w-full"
            @keydown.enter="confirmCreateJourney"
          />
        </label>
        <p class="text-xs text-surface-500">{{ t("ai_cards.create_journey.modal_hint") }}</p>
        <div class="flex items-center justify-end gap-2">
          <button class="btn-secondary" @click="journeyApply = null">{{ "Отмена" }}</button>
          <button
            class="btn-primary inline-flex items-center gap-1.5"
            :disabled="applyingJourney"
            @click="confirmCreateJourney"
          >
            {{ applyingJourney ? "Сохранение…" : t("ai_cards.create_journey.apply") }}
          </button>
        </div>
      </div>
    </div>

    <!-- D3 — publish_survey approve modal («Применить» on a survey draft) -->
    <div
      v-if="surveyApply"
      data-test="survey-apply-modal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-surface-900/40 p-4"
      @click.self="surveyApply = null"
    >
      <div class="card p-5 w-full max-w-md space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-surface-900">{{ t("ai_cards.publish_survey.modal_title") }}</h2>
          <button class="text-surface-400 hover:text-surface-700" @click="surveyApply = null">
            <X class="size-5" />
          </button>
        </div>
        <label class="block">
          <span class="text-sm font-medium text-surface-700 mb-1 block">{{ t("ai_cards.publish_survey.name_label") }}</span>
          <input
            v-model="surveyApply.name"
            type="text"
            class="input w-full"
            @keydown.enter="confirmPublishSurvey"
          />
        </label>
        <p class="text-xs text-surface-500">{{ t("ai_cards.publish_survey.modal_hint") }}</p>
        <div class="flex items-center justify-end gap-2">
          <button class="btn-secondary" @click="surveyApply = null">{{ "Отмена" }}</button>
          <button
            class="btn-primary inline-flex items-center gap-1.5"
            :disabled="applyingSurvey"
            @click="confirmPublishSurvey"
          >
            {{ applyingSurvey ? "Сохранение…" : t("ai_cards.publish_survey.apply") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Rendered assistant Markdown (v-html). Theme-aware via CSS vars → readable in
   light + dark. Tight spacing so chat bubbles stay compact. */
.md-content :deep(p) { margin: 0.35em 0; }
.md-content :deep(p:first-child) { margin-top: 0; }
.md-content :deep(p:last-child) { margin-bottom: 0; }
.md-content :deep(strong), .md-content :deep(b) { font-weight: 600; }
.md-content :deep(ul), .md-content :deep(ol) { margin: 0.35em 0; padding-left: 1.3rem; }
.md-content :deep(ul) { list-style: disc; }
.md-content :deep(ol) { list-style: decimal; }
.md-content :deep(li) { margin: 0.15em 0; }
.md-content :deep(h1), .md-content :deep(h2), .md-content :deep(h3),
.md-content :deep(h4), .md-content :deep(h5), .md-content :deep(h6) {
  font-weight: 600; margin: 0.6em 0 0.3em; line-height: 1.25;
}
.md-content :deep(h1) { font-size: 1.15em; }
.md-content :deep(h2) { font-size: 1.1em; }
.md-content :deep(h3) { font-size: 1.05em; }
.md-content :deep(a) { color: var(--color-accent); text-decoration: underline; }
.md-content :deep(code) {
  background: var(--color-bg-hover); color: var(--color-text);
  padding: 0.1rem 0.3rem; border-radius: 0.25rem; font-size: 0.85em;
}
.md-content :deep(pre) {
  background: var(--color-bg-hover); padding: 0.6rem 0.75rem; border-radius: 0.4rem;
  overflow-x: auto; margin: 0.5em 0;
}
.md-content :deep(pre code) { background: transparent; padding: 0; font-size: 0.85em; }
.md-content :deep(blockquote) {
  border-left: 3px solid var(--color-border); padding-left: 0.75rem;
  margin: 0.5em 0; color: var(--color-text-muted);
}
.md-content :deep(table) { border-collapse: collapse; margin: 0.5em 0; font-size: 0.9em; }
.md-content :deep(th), .md-content :deep(td) {
  border: 1px solid var(--color-border); padding: 0.25rem 0.5rem; text-align: left;
}
.md-content :deep(hr) { border: 0; border-top: 1px solid var(--color-border); margin: 0.6em 0; }
</style>
